/**
 * SudoGate — Re-autenticación para acciones sensibles
 *
 * Patrón de seguridad usado por:
 *   • GitHub    → "Sudo Mode"
 *   • Google    → "Confirmar identidad"
 *   • Instagram → "Confirmar tu contraseña"
 *
 * Flujo:
 *  1. Usuario navega a Configuración → ve pantalla de bloqueo
 *  2. Ingresa contraseña actual → POST /api/auth/verify-password
 *  3. Backend verifica (rate-limit 5/15 min) + password_verify()
 *  4. Éxito: se almacena estado en sessionStorage (limpiado al cerrar pestaña)
 *  5. Sesión segura activa 15 minutos con contador visible
 *  6. Al expirar → pantalla de bloqueo de nuevo
 */
import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  IconButton,
  InputAdornment,
  CircularProgress,
  LinearProgress,
} from "@mui/material";
import {
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Security as SecurityIcon,
  LockOutlined as LockOutlinedIcon,
} from "@mui/icons-material";
import api from "../../services/api";

// ── Constantes ────────────────────────────────────────────────────────────────
const SUDO_DURATION_MS = 15 * 60 * 1000; // 15 minutos
const SUDO_KEY         = "sudo_session";   // clave en sessionStorage (no localStorage)
// eslint-disable-next-line no-unused-vars
const MAX_ATTEMPTS     = 5;

// ── Helpers ──────────────────────────────────────────────────────────────────
const getSudoState = () => {
  try {
    const raw = sessionStorage.getItem(SUDO_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const sudoIsActive = () => {
  const s = getSudoState();
  if (!s?.verified || !s?.verifiedAt) return false;
  return Date.now() - s.verifiedAt < SUDO_DURATION_MS;
};

const getRemainingSeconds = () => {
  const s = getSudoState();
  if (!s) return 0;
  return Math.max(0, Math.floor((SUDO_DURATION_MS - (Date.now() - s.verifiedAt)) / 1000));
};

const formatTime = (secs) => {
  const m = Math.floor(secs / 60);
  const s = String(secs % 60).padStart(2, "0");
  return `${m}:${s}`;
};

// ─────────────────────────────────────────────────────────────────────────────
const SudoGate = ({ children }) => {
  const [unlocked,        setUnlocked]        = useState(sudoIsActive);
  const [password,        setPassword]        = useState("");
  const [showPassword,    setShowPassword]    = useState(false);
  const [loading,         setLoading]         = useState(false);
  const [error,           setError]           = useState("");
  const [attempts,        setAttempts]        = useState(0);
  const [remainingSeconds, setRemainingSeconds] = useState(getRemainingSeconds);
  const [blocked,         setBlocked]         = useState(false);

  // ── Countdown ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!unlocked) return;

    setRemainingSeconds(getRemainingSeconds());

    const timer = setInterval(() => {
      const rem = getRemainingSeconds();
      setRemainingSeconds(rem);
      if (rem <= 0) {
        sessionStorage.removeItem(SUDO_KEY);
        setUnlocked(false);
        setPassword("");
        setAttempts(0);
        setError("");
        setBlocked(false);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [unlocked]);

  // ── Verificar contraseña ───────────────────────────────────────────────────
  const handleVerify = useCallback(async () => {
    if (!password.trim()) {
      setError("Ingresa tu contraseña actual");
      return;
    }
    if (blocked) return;

    setLoading(true);
    setError("");

    try {
      await api.post("/auth/verify-password", { password });

      // Guardar estado en sessionStorage (se limpia al cerrar la pestaña)
      sessionStorage.setItem(SUDO_KEY, JSON.stringify({
        verified:   true,
        verifiedAt: Date.now(),
      }));

      setUnlocked(true);
      setPassword("");
      setAttempts(0);
    } catch (err) {
      const data        = err.response?.data ?? {};
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);

      let msg = data.message || "Contraseña incorrecta";

      // Si el servidor informa intentos restantes, mostrarlo
      if (typeof data.intentos_restantes === "number") {
        msg = data.intentos_restantes > 0
          ? `${msg}. Intentos restantes: ${data.intentos_restantes}`
          : `${msg}. Cuenta bloqueada 15 minutos.`;
      }

      // Bloqueo local por rate limit del servidor
      if (err.response?.status === 429) {
        setBlocked(true);
        msg = data.message || "Demasiados intentos. Espera 15 minutos.";
      }

      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [password, attempts, blocked]);

  // ── Bloquear manualmente ───────────────────────────────────────────────────
  const handleLock = () => {
    sessionStorage.removeItem(SUDO_KEY);
    setUnlocked(false);
    setPassword("");
    setAttempts(0);
    setError("");
    setBlocked(false);
  };

  const progressPct = (remainingSeconds / (SUDO_DURATION_MS / 1000)) * 100;
  const isAlmostExpired = remainingSeconds < 120;

  // ── ✅ Desbloqueado: mostrar contenido + badge ─────────────────────────────
  if (unlocked) {
    return (
      <Box sx={{ position: "relative" }}>
        {/* Badge flotante — sesión segura activa */}
        <Box
          sx={{
            position: "fixed",
            bottom: { xs: 16, sm: 24 },
            right:  { xs: 16, sm: 24 },
            zIndex: 1300,
          }}
        >
          <Paper
            elevation={8}
            onClick={handleLock}
            title="Haz clic para bloquear ahora"
            sx={{
              px: 2, py: 1,
              background:    "linear-gradient(135deg, rgba(0,25,15,0.97) 0%, rgba(0,45,25,0.97) 100%)",
              border:        `1px solid ${isAlmostExpired ? "rgba(255,100,100,0.4)" : "rgba(82,255,160,0.3)"}`,
              borderRadius:  3,
              backdropFilter:"blur(12px)",
              cursor:        "pointer",
              transition:    "border-color 0.4s",
              userSelect:    "none",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <LockOpenIcon sx={{ fontSize: 15, color: isAlmostExpired ? "#ff6b6b" : "#52FFA0" }} />
              <Typography
                sx={{
                  fontSize:   "0.77rem",
                  fontWeight: 700,
                  color:      isAlmostExpired ? "#ff9090" : "#52FFA0",
                  whiteSpace: "nowrap",
                }}
              >
                Sesión segura · {formatTime(remainingSeconds)}
              </Typography>
              <LockOutlinedIcon
                sx={{ fontSize: 13, color: "rgba(255,255,255,0.3)", ml: 0.5 }}
                title="Bloquear"
              />
            </Box>
            <LinearProgress
              variant="determinate"
              value={progressPct}
              sx={{
                mt: 0.5, height: 3, borderRadius: 2,
                bgcolor: "rgba(255,255,255,0.07)",
                "& .MuiLinearProgress-bar": {
                  bgcolor:      isAlmostExpired ? "#ff6b6b" : "#52FFA0",
                  borderRadius: 2,
                  transition:   "background-color 0.4s",
                },
              }}
            />
          </Paper>
        </Box>

        {children}
      </Box>
    );
  }

  // ── 🔒 Bloqueado: mostrar pantalla de verificación ────────────────────────
  return (
    <Box
      sx={{
        minHeight:       "calc(100vh - 120px)",
        display:         "flex",
        alignItems:      "center",
        justifyContent:  "center",
        p:               2,
        background:      "radial-gradient(ellipse at 50% 40%, rgba(255,215,0,0.05) 0%, transparent 65%)",
      }}
    >
      <Paper
        elevation={16}
        sx={{
          maxWidth:      440,
          width:         "100%",
          p:             { xs: 3, sm: 5 },
          textAlign:     "center",
          background:    "linear-gradient(160deg, rgba(11,15,30,0.99) 0%, rgba(18,25,48,0.99) 100%)",
          border:        "1px solid rgba(255,215,0,0.12)",
          borderRadius:  4,
          backdropFilter:"blur(24px)",
          boxShadow:     "0 24px 80px rgba(0,0,0,0.6), 0 0 60px rgba(255,215,0,0.06)",
        }}
      >
        {/* Ícono candado */}
        <Box
          sx={{
            width:        80,
            height:       80,
            borderRadius: "50%",
            background:   "linear-gradient(135deg, rgba(255,215,0,0.14) 0%, rgba(255,180,0,0.05) 100%)",
            border:       "2px solid rgba(255,215,0,0.28)",
            display:      "flex",
            alignItems:   "center",
            justifyContent:"center",
            mx:           "auto",
            mb:           3,
            boxShadow:    "0 0 48px rgba(255,215,0,0.14)",
          }}
        >
          <LockIcon sx={{ fontSize: 40, color: "#FFD700" }} />
        </Box>

        {/* Título */}
        <Typography
          variant="h5"
          sx={{ fontWeight: 800, mb: 1, color: "#fff", letterSpacing: "-0.01em" }}
        >
          Confirma tu identidad
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "rgba(255,255,255,0.5)", mb: 3, lineHeight: 1.65 }}
        >
          Por seguridad, verifica tu contraseña antes de acceder a la
          configuración de tu cuenta.
        </Typography>

        {/* Info card */}
        <Alert
          severity="info"
          icon={<SecurityIcon sx={{ fontSize: 18 }} />}
          sx={{
            mb:     3,
            textAlign: "left",
            bgcolor:"rgba(0,180,255,0.06)",
            border: "1px solid rgba(0,180,255,0.15)",
            borderRadius: 2,
            "& .MuiAlert-message": { fontSize: "0.8rem", lineHeight: 1.6 },
          }}
        >
          La sesión segura estará activa durante <strong>15 minutos</strong>{" "}
          tras verificar. Máximo <strong>5 intentos</strong> cada 15 minutos.
        </Alert>

        {/* Error */}
        {error && (
          <Alert
            severity={blocked ? "warning" : "error"}
            sx={{ mb: 2, textAlign: "left", borderRadius: 2 }}
          >
            {error}
          </Alert>
        )}

        {/* Campo contraseña */}
        <TextField
          fullWidth
          type={showPassword ? "text" : "password"}
          label="Contraseña actual"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            if (error) setError("");
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !loading && !blocked) handleVerify();
          }}
          disabled={loading || blocked}
          autoComplete="current-password"
          autoFocus
          sx={{ mb: 2.5 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword((p) => !p)}
                  edge="end"
                  tabIndex={-1}
                  size="small"
                >
                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* Botón verificar */}
        <Button
          fullWidth
          variant="contained"
          size="large"
          onClick={handleVerify}
          disabled={loading || blocked || !password.trim()}
          startIcon={
            loading
              ? <CircularProgress size={18} color="inherit" />
              : <LockOpenIcon />
          }
          sx={{
            py:         1.5,
            fontWeight: 800,
            fontSize:   "1rem",
            letterSpacing: "0.01em",
            borderRadius: 2,
            background:   blocked
              ? "rgba(255,255,255,0.04)"
              : "linear-gradient(90deg, #B8860B 0%, #FFD700 50%, #B8860B 100%)",
            backgroundSize: "200% auto",
            color: "#000",
            transition: "background-position 0.4s",
            "&:hover": {
              backgroundPosition: "right center",
              background: "linear-gradient(90deg, #FFD700 0%, #FFF176 50%, #FFD700 100%)",
            },
            "&.Mui-disabled": {
              background: "rgba(255,255,255,0.05)",
              color:      "rgba(255,255,255,0.2)",
            },
          }}
        >
          {loading ? "Verificando..." : blocked ? "Cuenta bloqueada" : "Verificar identidad"}
        </Button>

        {/* Nota de privacidad */}
        <Typography
          variant="caption"
          sx={{
            display:    "block",
            mt:         3,
            color:      "rgba(255,255,255,0.2)",
            lineHeight: 1.6,
            fontSize:   "0.72rem",
          }}
        >
          🔐 La contraseña nunca se almacena en este dispositivo.
          <br />
          Verificación segura cifrada con tu servidor.
        </Typography>
      </Paper>
    </Box>
  );
};

export default SudoGate;
